'use client'

import { useQuery } from '@tanstack/react-query'
import { gql } from 'graphql-request'
import React, { useEffect } from 'react'

import { useGraphQLClient } from '@/hooks/useGraphQLClient'
import { setSmsMarketingConfig } from '@/hooks/useSmsMarketingEnabled'
import { brand } from '@/utils/brand'

const GET_BRAND_SMS_MARKETING_ENABLED = gql`
  query GetBrandSmsMarketingEnabled($brandId: String!) {
    getBrand(brandId: $brandId) {
      smsMarketingEnabled
    }
  }
`

/**
 * Sole owner of the SMS-marketing brand flag: fetches the current brand's
 * `smsMarketingEnabled` once and publishes it to the module singleton in
 * useSmsMarketingEnabled.ts. Mounted exactly once in AppProviders; renders
 * nothing. Every useSmsMarketingEnabled()/isSmsMarketingEnabled() caller is a
 * pure consumer (mirrors RudderstackAnalyticsBootstrap).
 */
const SmsMarketingConfigBootstrap: React.FC = () => {
  const client = useGraphQLClient()

  const { data } = useQuery<boolean, Error>({
    queryKey: ['brandSmsMarketingEnabled', brand.key],
    queryFn: async () => {
      const { getBrand } = await client.request<{
        getBrand: { smsMarketingEnabled: boolean } | null
      }>(GET_BRAND_SMS_MARKETING_ENABLED, { brandId: brand.key })
      return getBrand?.smsMarketingEnabled ?? false
    },
    enabled: !!brand.key,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  })

  useEffect(() => {
    if (data !== undefined) setSmsMarketingConfig(data)
  }, [data])

  return null
}

export default SmsMarketingConfigBootstrap
